import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { api, PROD_STATUS_LABEL } from "@/lib/api";
import { Card, PageHeader, Badge, Btn } from "@/components/UI";
import { Package, ClipboardList, Factory, ShieldCheck, Truck, AlertTriangle, Clock, Boxes, ArrowRight, ListTodo, Euro, TrendingUp, Target, ChevronRight, CalendarClock, UserCheck, Cake, Award, PlusCircle } from "lucide-react";
import PersonnelQuickAction from "@/components/PersonnelQuickAction";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Link } from "react-router-dom";
import GMPCalendar from "@/components/GMPCalendar";
import ChecksCalendar from "@/components/ChecksCalendar";
import UyarilarWidget from "@/components/UyarilarWidget";
import MeetingsSummaryCard from "@/components/MeetingsSummaryCard";
import { useWidgetOrder, WidgetWrapper, WidgetEditButton, SIZE_COL_CLASS, useWidgetSize } from "@/components/WidgetReorder";
import ERPAssistantWidget from "@/components/ERPAssistantWidget";
import StorageHealthPills from "@/components/StorageHealthPills";
import { useAuth } from "@/lib/auth";
import { useLiveEvents } from "@/lib/events";

// iter273 — Karar Panosu için önceliğe göre kart kenar rengi.
// Değerler Production.jsx'teki PRIORITY_BORDER ile senkron.
const PRIORITY_LEFT_BORDER = {
  urgent: "border-l-4 border-l-red-500",
  high: "border-l-4 border-l-pink-400",
  normal: "border-l-4 border-l-orange-400",
  low: "border-l-4 border-l-yellow-400",
};

const cards = [
  { key: "open_orders", label: "Açık Siparişler", icon: ClipboardList, to: "/sales-orders", color: "text-indigo-600 dark:text-indigo-400" },
  { key: "production_today", label: "Aktif Üretim", icon: Factory, to: "/production", color: "text-violet-600 dark:text-violet-400" },
  { key: "waiting_qc_raw", label: "Ham Madde QC", icon: ShieldCheck, to: "/quality", color: "text-amber-600 dark:text-amber-400" },
  { key: "waiting_qc_bulk", label: "Bulk QC Bekliyor", icon: ShieldCheck, to: "/quality", color: "text-amber-600 dark:text-amber-400" },
  { key: "waiting_qc_finished", label: "Bitmiş Ürün QC", icon: ShieldCheck, to: "/quality", color: "text-amber-600 dark:text-amber-400" },
  { key: "today_shipments", label: "Bugünün Sevkiyatları", icon: Truck, to: "/shipments", color: "text-emerald-600 dark:text-emerald-400" },
  { key: "critical_stock", label: "Kritik Stok", icon: AlertTriangle, to: "/inventory", color: "text-orange-600 dark:text-orange-400" },
  { key: "missing_stock", label: "Yetersiz Stok", icon: AlertTriangle, to: "/inventory", color: "text-red-600 dark:text-red-400" },
  { key: "expiring_soon", label: "SKT Yaklaşan", icon: Clock, to: "/inventory", color: "text-amber-600 dark:text-amber-400" },
  { key: "delayed_orders", label: "Geciken Siparişler", icon: AlertTriangle, to: "/sales-orders", color: "text-red-600 dark:text-red-400" },
];

const periodOptions = [
  { key: 30, label: "Son 1 Ay", months: 1 },
  { key: 180, label: "Son 6 Ay", months: 6 },
  { key: 365, label: "Son 1 Yıl", months: 12 },
];

function PreviewCard({ title, icon: Icon, to, rows, columns, testid, empty = "Kayıt yok" }) {
  // Size-aware row cap so small widgets don't overflow. Read from context so
  // parent WidgetWrapper's size flows in without a DOM-prop leak.
  const widgetSize = useWidgetSize();
  const rowCap = widgetSize === "S" ? 2 : widgetSize === "M" ? 3 : widgetSize === "L" ? 5 : 8;
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 gap-2 min-w-0">
        <h3 className="text-sm font-semibold flex items-center gap-2 text-slate-800 dark:text-slate-100 min-w-0 truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <Icon size={14} className="text-indigo-600 dark:text-indigo-400 shrink-0" /> <span className="truncate">{title}</span>
        </h3>
        <Link to={to} className="shrink-0">
          <Btn size="sm" variant="secondary" data-testid={`${testid}-goto`}>Tümü <ArrowRight size={12}/></Btn>
        </Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700/50">
            <tr>{columns.map((c, i) => <th key={i} className="text-left px-2 py-1.5 font-semibold text-slate-500 dark:text-slate-400 uppercase text-[10px]">{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {(!rows || rows.length === 0) && <tr><td colSpan={columns.length} className="text-center py-6 text-slate-400">{empty}</td></tr>}
            {(rows || []).slice(0, rowCap).map((r, i) => (
              <tr key={r.id || i} className="border-b border-slate-100 dark:border-slate-700/40 hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
                {columns.map((c, ci) => <td key={ci} className="px-2 py-1.5 text-slate-700 dark:text-slate-300 max-w-[220px] truncate">{c.render ? c.render(r) : r[c.key] || "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows && rows.length > rowCap && (
        <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1.5 text-center">
          +{rows.length - rowCap} kayıt daha — <Link to={to} className="underline">Tümünü Gör</Link>
        </div>
      )}
    </Card>
  );
}

// iter326 — Dashboard is the single canonical panel. The optional `scope`
// prop lets it be reused for the Kullanıcı Kontrol Paneli (same widgets,
// same canonical data; only the layout prefs document differs).
export default function Dashboard({ scope = "admin" }) {
  const { user } = useAuth();
  // [msg-145 Bölüm 3+4] Kârlılık widget'ları yalnızca Sistem Yöneticisi'ne görünür.
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({});
  const [requests, setRequests] = useState([]);
  const [prodOrders, setProdOrders] = useState([]);
  const [finished, setFinished] = useState([]);
  const [lots, setLots] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState({ eur_try_rate: 35 });
  const [labor, setLabor] = useState(null);
  const [formulas, setFormulas] = useState([]);
  const [trend, setTrend] = useState(null);
  const [period, setPeriod] = useState(180); // days
  const [supplierDue, setSupplierDue] = useState({ suppliers: [], loaded: false });
  // iter143 — Yaklaşan Vadeler birleşik widget: portföydeki müşteri çekleri (source-of-truth: iter142)
  const [chequePortfolio, setChequePortfolio] = useState({ cheques: [], loaded: false });
  // iter144.b — Kritik Raf Ömrü Lotları widget (source-of-truth: iter144 /inventory/shelf-life-alerts)
  const [shelfLifeAlerts, setShelfLifeAlerts] = useState({ alerts: [], count: 0, loaded: false });
  // iter109: Personel dashboard state.
  const [personnelAlerts, setPersonnelAlerts] = useState(null);
  const [personnelQuickOpen, setPersonnelQuickOpen] = useState(false);
  const [personnelDetail, setPersonnelDetail] = useState(null);
  // iter239.1 — Multi-currency FX converter (TRY/EUR/USD) backed by the
  // canonical /api/fx/convert TCMB endpoint. Replaces the earlier
  // TRY↔EUR-only widget without changing widget id, size or layout.
  const [fxAmount, setFxAmount] = useState(100);
  const [fxBase, setFxBase] = useState("EUR");
  const [fxQuote, setFxQuote] = useState("TRY");
  const [fxResult, setFxResult] = useState(null);   // { converted, rate, rate_date, source }
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(setData).catch(() => {});
    api.get("/requests").then(setRequests).catch(() => {});
    api.get("/production-orders").then(setProdOrders).catch(() => {});
    api.get("/finished-lots").then(setFinished).catch(() => {});
    api.get("/lots").then(setLots).catch(() => {});
    api.get("/products").then(setProducts).catch(() => {});
    api.get("/customers").then(setCustomers).catch(() => {});
    api.get("/materials").then(setMaterials).catch(() => {});
    api.get("/sales-orders").then(setOrders).catch(() => {});
    api.get("/settings").then(s => setSettings(s)).catch(() => {});
    api.get("/formulas").then(setFormulas).catch(() => {});
    // iter108 → iter143: Vade uyarı widget kaynağı — mevcut supplier-balance-tracking read-only aggregate.
    api.get("/accounting/supplier-balance-tracking")
       .then(r => setSupplierDue({ suppliers: r.suppliers || [], loaded: true }))
       .catch(() => setSupplierDue({ suppliers: [], loaded: true }));
    // iter143 — Yaklaşan çekler kaynağı: authoritative portfolio endpoint (portfolio=true filter server-side)
    api.get("/accounting/cheques/portfolio")
       .then(r => setChequePortfolio({ cheques: r?.cheques || [], loaded: true }))
       .catch(() => setChequePortfolio({ cheques: [], loaded: true }));
    // iter144.b — Kritik raf ömrü lotları (iter144 authoritative endpoint, read-only projection)
    api.get("/inventory/shelf-life-alerts")
       .then(r => setShelfLifeAlerts({ alerts: r?.alerts || [], count: r?.count || 0, loaded: true }))
       .catch(() => setShelfLifeAlerts({ alerts: [], count: 0, loaded: true }));
    // iter109: Personnel alerts (birthdays, upcoming leaves, anniversaries).
    api.get("/personnel/alerts")
       .then(setPersonnelAlerts)
       .catch(() => setPersonnelAlerts({ birthdays: [], anniversaries: [], upcoming_leaves: [], counts: {} }));
  }, []);

  useEffect(() => {
    const opt = periodOptions.find(p => p.key === period) || periodOptions[1];
    api.get("/finance/labor-per-unit", { days: opt.key }).then(setLabor).catch(() => {});
    api.get("/finance/profit-trend", { months: opt.months }).then(setTrend).catch(() => {});
  }, [period]);

  const rate = parseFloat(settings.eur_try_rate) || 35;

  // iter239.1 — Debounced conversion so typing an amount does NOT hit the
  // API on every keystroke. Same-currency short-circuits without a call.
  // No page reload; widget size + Dashboard state are preserved.
  useEffect(() => {
    const amt = parseFloat(fxAmount);
    if (!isFinite(amt)) return;
    if (fxBase === fxQuote) {
      setFxResult({ converted: amt, rate: 1, rate_date: fxResult?.rate_date || "",
                     source: "TCMB", rate_type: "ForexSelling" });
      setFxError(null);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setFxLoading(true); setFxError(null);
      try {
        const r = await api.get("/fx/convert",
          { amount: amt, base: fxBase, quote: fxQuote },
          { signal: ctrl.signal });
        setFxResult(r);
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
          setFxError(e.response?.data?.detail || "Kur alınamadı");
        }
      } finally { setFxLoading(false); }
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [fxAmount, fxBase, fxQuote]);

  const pname = id => products.find(p => p.id === id)?.name || "—";
  const cname = id => customers.find(c => c.id === id)?.legal_name || "—";
  const mname = id => materials.find(m => m.id === id)?.name || "—";

  // [iter73 msg 98-madde Bölüm 1] Karar Panosu 3 canlı özet paneli:
  //   Panel 1: Aktif Üretim Emirleri  · Panel 2: Teslim Bekleyen Bitmiş Ürünler  · Panel 3: Satın Alma İhtiyaçları (iter272)
  // Kaynak: mevcut Production Orders + Finished Lots + /purchasing/material-needs
  //         (yeni hesaplama motoru yok — MRP zaten phantom rezervasyon üzerinden hesaplıyor).
  // iter272 — Aktif üretim emri kümesi genişletildi: son onay verilmiş VE iptal
  // edilmiş dışında her aşama dahil edildi. status alanı Production.jsx tarafında
  // set edilen tüm mevcut değerleri kapsar. `final_approved_at` set ise gizlenir.
  const ACTIVE_PROD_STATUS = new Set([
    "draft", "waiting_materials", "ready", "weighing",
    "in_production", "awaiting_qc_bulk", "awaiting_qc",
    "ready_for_filling", "filling", "packaging",
    "awaiting_qc_finished", "completed",
  ]);
  const activeProdOrders = prodOrders.filter(p =>
    p && !p.is_deleted && !p.archived
    && p.status !== "cancelled"
    && !p.final_approved_at
    && ACTIVE_PROD_STATUS.has(p.status),
  );

  // Teslim bekleyen: finished lots with qty_available > 0 (henüz tamamı sevk edilmemiş).
  // qty_available == 0 → tümü sevk edildi, listeden düşer. iter272 — slice kaldırıldı.
  const awaitingDelivery = finished.filter(fl => (fl.qty_available || 0) > 0);

  // iter272 — Satın Alma İhtiyaçları (Panel 3): İhtiyaç Listesi endpoint'iyle
  // birebir aynı veri; ayrı hesaplama YOK.
  const [materialNeeds, setMaterialNeeds] = useState([]);
  const fetchNeeds = useCallback(async () => {
    try {
      const r = await api.get("/purchasing/material-needs");
      setMaterialNeeds(Array.isArray(r) ? r : []);
    } catch { setMaterialNeeds([]); }
  }, []);
  useEffect(() => { fetchNeeds(); }, [fetchNeeds]);

  // iter273 — SSE-tabanlı Karar Panosu otomatik yenileme (30 sn polling YERİNE).
  // Debounce ile aynı burst içindeki çoklu event'ler tek fetch'te toplanır.
  const refreshTimers = useRef({});
  const debouncedRefresh = useCallback((key, fn, delay = 800) => {
    if (refreshTimers.current[key]) clearTimeout(refreshTimers.current[key]);
    refreshTimers.current[key] = setTimeout(() => {
      fn();
      refreshTimers.current[key] = null;
    }, delay);
  }, []);
  const refreshProd = useCallback(() => {
    api.get("/production-orders").then(setProdOrders).catch(() => {});
  }, []);
  const refreshFinished = useCallback(() => {
    api.get("/finished-lots").then(setFinished).catch(() => {});
  }, []);
  useEffect(() => () => {
    // component unmount → outstanding timer'ları temizle
    Object.values(refreshTimers.current).forEach(t => t && clearTimeout(t));
  }, []);
  // Panel 1 (Aktif Üretim): production_order.* + sales_order.approved (yeni PO doğar)
  useLiveEvents(
    ["production_order.approved", "production_order.completed", "production_order.updated",
     "production_order.created", "production_order.cancelled",
     "sales_order.approved", "sales_order.cancelled"],
    () => debouncedRefresh("prod", refreshProd),
  );
  // Panel 2 (Teslim Bekleyen): finished lots — üretim tamamlanınca oluşur, sevkiyat/teslimat düşürür
  useLiveEvents(
    ["production_order.completed", "finished_lot.created", "finished_lot.updated",
     "shipment.created", "shipment.updated", "delivery.completed"],
    () => debouncedRefresh("finished", refreshFinished),
  );
  // Panel 3 (Satın Alma İhtiyaçları): SO/PO/stok değişiklikleri
  useLiveEvents(
    ["sales_order.approved", "sales_order.cancelled", "sales_order.updated",
     "purchase_order.created", "purchase_order.updated", "purchase_order.approved",
     "purchase_order.cancelled", "purchase_order.received",
     "inventory.lot.approved", "inventory.lot.adjusted", "inventory.consumed"],
    () => debouncedRefresh("needs", fetchNeeds),
  );

  const todoTotal = activeProdOrders.length + awaitingDelivery.length + materialNeeds.length;

  // Dashboard widget reorder — persisted per-user via backend
  const DEFAULT_WIDGET_ORDER = ["assistant", "reminders", "todo", "supplier_due", "shelf_life", "personnel", "meetings", "fx", "kpi", "checks", "previews", "profit_period", "profit_summary", "profit_trend", "gmp"];
  const DEFAULT_WIDGET_SIZES = {
    assistant: "L", todo: "FULL", supplier_due: "FULL", shelf_life: "FULL",
    personnel: "FULL", meetings: "FULL", fx: "M", kpi: "FULL", checks: "FULL",
    previews: "FULL", profit_period: "FULL", profit_summary: "FULL",
    profit_trend: "FULL", gmp: "FULL", reminders: "FULL",
  };
  const WIDGET_TITLES = {
    assistant: "ERP Yardımcı",
    todo: "Karar Panosu", supplier_due: "Yaklaşan Vadeler", shelf_life: "Kritik Raf Ömrü Lotları", personnel: "Personel", meetings: "Toplantı Notları", fx: "Kur Dönüştürücü", kpi: "KPI Kartları",
    checks: "Çek Takvimi & Notlar",
    previews: "Liste Önizlemeleri", profit_period: "Kârlılık — Dönem", profit_summary: "Kârlılık Özeti",
    profit_trend: "Kârlılık Trendi", gmp: "GMP Takvim", reminders: "Uyarılar",
  };
  const { order, hidden, sizes, editMode, setEditMode, move, reorderTo, toggleHidden, cycleSize, resetOrder, canEdit } = useWidgetOrder(DEFAULT_WIDGET_ORDER, DEFAULT_WIDGET_SIZES, scope);

  // Drag-drop state (native HTML5)
  const [dragKey, setDragKey] = React.useState(null);
  const onDragStart = (k) => (e) => { setDragKey(k); try { e.dataTransfer.setData("text/plain", k); } catch {} e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (k) => (e) => { e.preventDefault(); const src = dragKey || e.dataTransfer.getData("text/plain"); reorderTo(src, k); setDragKey(null); };

  const widgets = {
    assistant: <ERPAssistantWidget size={sizes.assistant || "L"} />,
    supplier_due: (() => {
      // iter143: YAKLAŞAN VADELER — birleşik widget (ÇEK + ÖDEME).
      // ÖDEME kaynağı: iter104 /accounting/supplier-balance-tracking (source-of-truth, DEĞİŞMEDİ)
      // ÇEK kaynağı:  iter142 /accounting/cheques/portfolio (portfolio=true filter server-side)
      // Read-only projection. Business transaction bu widget üzerinden YAPILMAZ.
      const todayMs = (() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t.getTime(); })();
      const daysUntil = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date((dateStr || "").slice(0, 10));
        if (isNaN(d.getTime())) return null;
        return Math.round((d.getTime() - todayMs) / 86400000);
      };
      const money = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      // ÖDEME (Type=ÖDEME): mevcut supplier davranışını KORU — worst_days_left ≤ 7 filter aynı
      const supplierItems = (supplierDue.suppliers || [])
        .filter(s => s.has_due_warning && typeof s.worst_days_left === "number" && s.worst_days_left <= 7)
        .map(s => ({
          key: `supplier-${s.supplier_id}`,
          type: "ÖDEME",
          name: s.supplier_name,
          amount: Number(s.balance || 0),
          days_left: s.worst_days_left,
          due_warnings: s.due_warnings,
          link: "/finance?tab=supplier_balance",
          testid: `upcoming-due-supplier-${s.supplier_id}`,
        }));

      // ÇEK (Type=ÇEK): portfolio=true çekler — 0-7 gün + vadesi geçmiş (portfolio=true kaldıysa dikkat)
      // NOT: Terminal statüdeki (ciro/factoring/tahsil edilmiş) çekler portfolio endpoint'inden
      // zaten geliyor — o endpoint transferred_to_supplier_at / actual_collection_date / factoring_id
      // filter'ı ile portfolio=false çekleri backend'de dışarıda bırakıyor (iter142 source-of-truth).
      const chequeItems = (chequePortfolio.cheques || [])
        .map(c => ({ ...c, _days_left: daysUntil(c.check_due_date) }))
        .filter(c => typeof c._days_left === "number" && c._days_left <= 7)
        .map(c => ({
          key: `cheque-${c.id}`,
          type: "ÇEK",
          name: c.customer_name || "Müşteri",
          amount: Number(c.amount || 0),
          days_left: c._days_left,
          due_date: (c.check_due_date || "").slice(0, 10),
          check_number: c.check_number,
          bank_name: c.bank_name,
          link: "/finance",
          testid: `upcoming-due-cheque-${c.id}`,
        }));

      // Vade tarihine göre kronolojik sıralama (en yakın vade en üstte). Vadesi geçmiş de en üstte.
      const combined = [...chequeItems, ...supplierItems].sort(
        (a, b) => (a.days_left ?? 999) - (b.days_left ?? 999),
      );
      const visible = combined.slice(0, 8);

      // Toplamlar — SADECE listede görünen (widget kriterine uyan) kayıtlar
      const totalCheque = chequeItems.reduce((s, i) => s + i.amount, 0);
      const totalPayment = supplierItems.reduce((s, i) => s + i.amount, 0);

      const loaded = supplierDue.loaded && chequePortfolio.loaded;

      if (!loaded || combined.length === 0) {
        return (
          <Card className="p-4 mb-4 border-slate-200 dark:border-slate-700" data-testid="supplier-due-widget-empty">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock size={16} className="text-slate-500"/>
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200" data-testid="upcoming-dues-title">Yaklaşan Vadeler (7 gün)</h3>
              <Link to="/finance?tab=supplier_balance" className="ml-auto"><Btn size="sm" variant="secondary" data-testid="supplier-due-goto">Tümü <ArrowRight size={12}/></Btn></Link>
            </div>
            <div className="text-xs text-slate-400 py-2">Önümüzdeki 7 gün içinde vadesi yaklaşan çek veya tedarikçi ödemesi yok.</div>
          </Card>
        );
      }

      const overdueCount = visible.filter(i => (i.days_left ?? 0) < 0).length;

      return (
        <Card className="p-4 mb-4 bg-gradient-to-r from-amber-50/70 via-orange-50/60 to-rose-50/70 dark:from-amber-900/20 dark:via-orange-900/20 dark:to-rose-900/20 border-amber-200/60 dark:border-amber-500/30" data-testid="supplier-due-widget">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock size={16} className="text-amber-700 dark:text-amber-400"/>
            <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-300" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="upcoming-dues-title">Yaklaşan Vadeler (7 gün)</h3>
            {overdueCount > 0 && <span className="text-[10px] bg-rose-100 dark:bg-rose-500/20 text-rose-800 dark:text-rose-300 border border-rose-300/50 px-1.5 py-0.5 rounded-full font-mono" data-testid="supplier-due-overdue-badge">{overdueCount} gecikmiş</span>}
            <Link to="/finance?tab=supplier_balance" className="ml-auto"><Btn size="sm" variant="secondary" data-testid="supplier-due-goto">Tümü <ArrowRight size={12}/></Btn></Link>
          </div>

          {/* Toplam özet — read-only projection */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="p-2 rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/70 dark:bg-emerald-900/20" data-testid="upcoming-dues-total-cheque">
              <div className="text-[10px] uppercase font-bold text-emerald-800 dark:text-emerald-300">Alınacak Çek</div>
              <div className="font-mono font-bold text-emerald-900 dark:text-emerald-200 text-sm">{money(totalCheque)} TL</div>
              <div className="text-[10px] text-emerald-700 dark:text-emerald-400">{chequeItems.length} çek</div>
            </div>
            <div className="p-2 rounded-md border border-rose-200 dark:border-rose-500/30 bg-rose-50/70 dark:bg-rose-900/20" data-testid="upcoming-dues-total-payment">
              <div className="text-[10px] uppercase font-bold text-rose-800 dark:text-rose-300">Yapılacak Ödeme</div>
              <div className="font-mono font-bold text-rose-900 dark:text-rose-200 text-sm">{money(totalPayment)} TL</div>
              <div className="text-[10px] text-rose-700 dark:text-rose-400">{supplierItems.length} tedarikçi</div>
            </div>
          </div>

          {/* Birleşik liste — vade tarihine göre kronolojik */}
          <div className="space-y-1.5" data-testid="upcoming-dues-list">
            {visible.map(item => {
              const overdue = (item.days_left ?? 0) < 0;
              const daysAbs = Math.abs(item.days_left);
              const isCheque = item.type === "ÇEK";
              return (
                <Link
                  key={item.key}
                  to={item.link}
                  className={`flex items-center gap-2 p-2 rounded-md border ${overdue ? "border-rose-300 bg-rose-50/80 dark:bg-rose-900/20 dark:border-rose-500/40" : "border-slate-200 bg-white/70 dark:bg-slate-800/60 dark:border-slate-600"} hover:shadow-md transition`}
                  data-testid={item.testid}
                >
                  <span
                    className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full border ${isCheque ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-300/60" : "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300/60"}`}
                    data-testid={`upcoming-due-type-${item.type === "ÇEK" ? "cheque" : "payment"}`}
                  >
                    {item.type}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{item.name}</div>
                    {isCheque && (
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                        #{item.check_number || "—"} · {item.bank_name || "—"} · Vade {item.due_date}
                      </div>
                    )}
                    {!isCheque && Array.isArray(item.due_warnings) && item.due_warnings.length > 1 && (
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{item.due_warnings.length} vadeli alım</div>
                    )}
                  </div>
                  <span className="font-mono text-xs font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap">{money(item.amount)} TL</span>
                  <Badge level={overdue ? "missing" : "attention"}>
                    {overdue ? `${daysAbs}g gecikmiş` : item.days_left === 0 ? "Bugün" : `${daysAbs}g kaldı`}
                  </Badge>
                </Link>
              );
            })}
          </div>
          {combined.length > visible.length && (
            <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 text-center">
              +{combined.length - visible.length} kayıt daha — <Link to="/finance?tab=supplier_balance" className="underline">Tümünü Gör</Link>
            </div>
          )}
        </Card>
      );
    })(),
    shelf_life: (() => {
      // iter144.b — Kritik Raf Ömrü Lotları widget. Read-only projection from iter144
      // /inventory/shelf-life-alerts (authoritative: used_pct >= 75, qty_available > 0).
      // Backend server-side filter — frontend hesaplama yapmaz. SKT geçmişleri mevcut
      // _auto_expire_lots cron kuralında yönetilir (bu widget onu bypass etmez).
      const alerts = (shelfLifeAlerts.alerts || []);
      if (!shelfLifeAlerts.loaded || shelfLifeAlerts.count === 0) {
        return (
          <Card className="p-4 mb-4 border-slate-200 dark:border-slate-700" data-testid="shelf-life-widget-empty">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-slate-500"/>
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200" data-testid="shelf-life-widget-title">Kritik Raf Ömrü Lotları</h3>
              <Link to="/inventory?tab=lots" className="ml-auto"><Btn size="sm" variant="secondary" data-testid="shelf-life-widget-goto">Tümü <ArrowRight size={12}/></Btn></Link>
            </div>
            <div className="text-xs text-slate-400 py-2">Raf ömrünün %75'inden fazlasını tamamlamış aktif stok lotu bulunmuyor.</div>
          </Card>
        );
      }
      const expiredCount = alerts.filter(a => a.expired).length;
      const money = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return (
        <Card className="p-4 mb-4 bg-gradient-to-r from-orange-50/70 via-rose-50/60 to-red-50/70 dark:from-orange-900/20 dark:via-rose-900/20 dark:to-red-900/20 border-orange-200/60 dark:border-orange-500/30" data-testid="shelf-life-widget">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-orange-700 dark:text-orange-400"/>
            <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-300" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="shelf-life-widget-title">Kritik Raf Ömrü Lotları (%75+)</h3>
            <span className="text-[10px] bg-orange-100 dark:bg-orange-500/20 text-orange-800 dark:text-orange-300 border border-orange-300/50 px-1.5 py-0.5 rounded-full font-mono" data-testid="shelf-life-widget-count">{shelfLifeAlerts.count} lot</span>
            {expiredCount > 0 && <span className="text-[10px] bg-rose-100 dark:bg-rose-500/20 text-rose-800 dark:text-rose-300 border border-rose-300/50 px-1.5 py-0.5 rounded-full font-mono" data-testid="shelf-life-widget-expired-badge">{expiredCount} SKT geçmiş</span>}
            <Link to="/inventory?tab=lots" className="ml-auto"><Btn size="sm" variant="secondary" data-testid="shelf-life-widget-goto">Tümü <ArrowRight size={12}/></Btn></Link>
          </div>
          <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: "320px" }} data-testid="shelf-life-widget-list">
            {alerts.map(a => (
              <Link
                key={a.lot_id}
                to="/inventory?tab=lots"
                className={`flex items-center gap-2 p-2 rounded-md border ${a.expired ? "border-rose-300 bg-rose-50/80 dark:bg-rose-900/20 dark:border-rose-500/40" : "border-orange-200 bg-white/70 dark:bg-slate-800/60 dark:border-orange-500/30"} hover:shadow-md transition`}
                data-testid={`shelf-life-widget-item-${a.lot_id}`}
              >
                <span
                  className={`text-[10px] font-bold font-mono px-1.5 py-0.5 rounded-full border ${a.expired ? "bg-rose-100 dark:bg-rose-900/40 text-rose-900 dark:text-rose-300 border-rose-300" : "bg-orange-100 dark:bg-orange-900/40 text-orange-900 dark:text-orange-300 border-orange-300"}`}
                  data-testid={`shelf-life-widget-pct-${a.lot_id}`}
                >
                  %{a.used_pct}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{a.material_name}</div>
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate font-mono">
                    Lot {a.lot_code} · SKT {(a.expiry_date || "—").slice(0, 10)} · Stok {money(a.qty_available)} {a.unit}
                  </div>
                </div>
                <Badge level={a.expired ? "missing" : "attention"}>
                  {a.expired ? `${Math.abs(a.remaining_days)}g gecikmiş` : `${a.remaining_days}g kaldı`}
                </Badge>
              </Link>
            ))}
          </div>
          {shelfLifeAlerts.count > alerts.length && (
            <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 text-center">
              +{shelfLifeAlerts.count - alerts.length} lot daha — <Link to="/inventory?tab=lots" className="underline">Tümünü Gör</Link>
            </div>
          )}
        </Card>
      );
    })(),

    personnel: (() => {
      // iter109: Personel widget — 7-day alert window (birthdays / upcoming leaves / anniversaries).
      const a = personnelAlerts || { birthdays: [], anniversaries: [], upcoming_leaves: [], counts: {} };
      const bCount = a.birthdays?.length || 0;
      const lCount = a.upcoming_leaves?.length || 0;
      const wCount = a.anniversaries?.length || 0;
      return (
        <Card className="p-4 mb-4 bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 dark:from-violet-900/20 dark:via-purple-900/20 dark:to-fuchsia-900/20 border-violet-200/60 dark:border-violet-500/30" data-testid="personnel-widget">
          <div className="flex items-center gap-2 mb-3">
            <UserCheck size={16} className="text-violet-700 dark:text-violet-400"/>
            <h3 className="text-sm font-semibold text-violet-900 dark:text-violet-300" style={{ fontFamily: "'Manrope', sans-serif" }}>Personel</h3>
            <button onClick={() => setPersonnelQuickOpen(true)}
              className="ml-auto text-xs bg-gradient-to-r from-violet-600 to-purple-600 text-white px-3 py-1 rounded-full font-semibold hover:shadow-md flex items-center gap-1"
              data-testid="personnel-widget-quick-btn">
              <PlusCircle size={12}/> İşlem Ekle
            </button>
            <Link to="/personnel"><Btn size="sm" variant="secondary" data-testid="personnel-widget-goto">Detay <ArrowRight size={12}/></Btn></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
            <AlertPill icon={<Cake size={13}/>} label="Doğum Günü" count={bCount}
              onClick={() => bCount > 0 && setPersonnelDetail({ kind: "birthday", items: a.birthdays })}
              testid="personnel-alert-birthdays"/>
            <AlertPill icon={<CalendarClock size={13}/>} label="Yaklaşan İzin" count={lCount}
              onClick={() => lCount > 0 && setPersonnelDetail({ kind: "leave", items: a.upcoming_leaves })}
              testid="personnel-alert-leaves"/>
            <AlertPill icon={<Award size={13}/>} label="İş Yıldönümü" count={wCount}
              onClick={() => wCount > 0 && setPersonnelDetail({ kind: "anniversary", items: a.anniversaries })}
              testid="personnel-alert-anniversaries"/>
          </div>
        </Card>
      );
    })(),
    todo: (
      <Card className="p-4 mb-4 bg-gradient-to-r from-rose-50/70 via-fuchsia-50/70 to-violet-50/70 dark:from-rose-900/20 dark:via-fuchsia-900/20 dark:to-violet-900/20 border-rose-200/60 dark:border-fuchsia-500/30" data-testid="todo-board">
        <div className="flex items-center gap-2 mb-4">
          <Target size={18} className="text-rose-600 dark:text-rose-400"/>
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>Karar Panosu</h2>
          <span className="ml-auto text-xs bg-white/70 dark:bg-slate-800/70 px-2.5 py-0.5 rounded-full font-mono text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30">{todoTotal} kayıt</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Panel 1: Aktif Üretim Emirleri (iter272 — sınırsız + iç scroll) */}
          <div className="bg-white/70 dark:bg-slate-800/60 border border-violet-200/60 dark:border-violet-500/30 rounded-lg p-3 flex flex-col" style={{ maxHeight: "22rem" }} data-testid="panel-active-production">
            <Link to="/production" className="flex items-center justify-between mb-2 hover:bg-slate-50 dark:hover:bg-slate-800/40 rounded px-1 -mx-1" data-testid="panel-active-production-title">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700 dark:text-violet-400 flex items-center gap-1.5"><Factory size={12}/> Aktif Üretim Emirleri</div>
              <span className="text-[10px] bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded-full font-mono" data-testid="panel-active-production-count">{activeProdOrders.length}</span>
            </Link>
            <div className="overflow-y-auto flex-1 -mx-1 px-1" data-testid="panel-active-production-list">
              {activeProdOrders.length === 0 && <div className="text-xs text-slate-400 py-2">Aktif üretim emri yok</div>}
              {activeProdOrders.map(p => (
                <Link key={p.id} to={`/production/${p.id}`} className={`block py-1 pl-2 border-b border-slate-100 dark:border-slate-700/40 hover:bg-slate-50 dark:hover:bg-slate-800/40 ${PRIORITY_LEFT_BORDER[p.priority || "normal"] || ""}`} data-testid={`panel-po-${p.id}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono text-[10px] text-slate-500">{p.prod_no}</span>
                    <Badge level={p.status === "in_production" ? "attention" : "ok"}>{PROD_STATUS_LABEL[p.status] || p.status}</Badge>
                  </div>
                  <div className="text-xs text-slate-700 dark:text-slate-300 truncate">{cname(p.customer_id)} · {pname(p.product_id)}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{p.planned_units || "—"} adet{p.priority ? ` · ${p.priority}` : ""}</div>
                </Link>
              ))}
            </div>
          </div>
          {/* Panel 2: Teslim Bekleyen (iter272 — sınırsız + iç scroll) */}
          <div className="bg-white/70 dark:bg-slate-800/60 border border-emerald-200/60 dark:border-emerald-500/30 rounded-lg p-3 flex flex-col" style={{ maxHeight: "22rem" }} data-testid="panel-awaiting-delivery">
            <Link to="/finished-products" className="flex items-center justify-between mb-2 hover:bg-slate-50 dark:hover:bg-slate-800/40 rounded px-1 -mx-1" data-testid="panel-awaiting-delivery-title">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5"><Truck size={12}/> Teslim Bekleyen</div>
              <span className="text-[10px] bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full font-mono" data-testid="panel-awaiting-delivery-count">{awaitingDelivery.length}</span>
            </Link>
            <div className="overflow-y-auto flex-1 -mx-1 px-1" data-testid="panel-awaiting-delivery-list">
              {awaitingDelivery.length === 0 && <div className="text-xs text-slate-400 py-2">Teslim bekleyen ürün yok</div>}
              {awaitingDelivery.map(fl => {
                const partial = (fl.qty_available || 0) < (fl.qty_produced || 0);
                return (
                  <Link key={fl.id} to="/finished-products" className="block py-1 border-b border-slate-100 dark:border-slate-700/40 hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`panel-fl-${fl.id}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono text-[10px] text-slate-500">{fl.lot_code}</span>
                      <Badge level={partial ? "attention" : "ok"}>{partial ? "Kısmi Sevk" : "Hazır"}</Badge>
                    </div>
                    <div className="text-xs text-slate-700 dark:text-slate-300 truncate">{pname(fl.product_id)}</div>
                    <div className="text-[10px] text-slate-500 font-mono">Kalan: <b className="text-emerald-700 dark:text-emerald-300">{fl.qty_available}</b> / {fl.qty_produced}</div>
                  </Link>
                );
              })}
            </div>
          </div>
          {/* Panel 3: Satın Alma İhtiyaçları (iter272 — İhtiyaç Listesi ile birebir aynı veri) */}
          <div className="bg-white/70 dark:bg-slate-800/60 border border-orange-200/60 dark:border-orange-500/30 rounded-lg p-3 flex flex-col" style={{ maxHeight: "22rem" }} data-testid="panel-purchase-needs">
            <Link to="/purchasing?tab=needs" className="flex items-center justify-between mb-2 hover:bg-slate-50 dark:hover:bg-slate-800/40 rounded px-1 -mx-1" data-testid="panel-purchase-needs-title">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-orange-700 dark:text-orange-400 flex items-center gap-1.5"><AlertTriangle size={12}/> Satın Alma İhtiyaçları</div>
              <span className="text-[10px] bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded-full font-mono" data-testid="panel-purchase-needs-count">{materialNeeds.length}</span>
            </Link>
            <div className="overflow-y-auto flex-1 -mx-1 px-1" data-testid="panel-purchase-needs-list">
              {materialNeeds.length === 0 && <div className="text-xs text-slate-400 py-2">Net satın alma ihtiyacı yok</div>}
              {materialNeeds.map(r => {
                const prio = r.priority || "normal";
                const prioClass = prio === "urgent" ? "bg-red-100 text-red-700 border-red-300"
                  : prio === "low" ? "bg-amber-50 text-amber-700 border-amber-200"
                  : "bg-slate-100 text-slate-700 border-slate-300";
                const prioLabel = prio === "urgent" ? "Acil" : prio === "low" ? "Düşük" : "Normal";
                const firstSo = (r.sales_orders || [])[0];
                return (
                  <Link key={r.material_id} to="/purchasing?tab=needs" className="block py-1 border-b border-slate-100 dark:border-slate-700/40 hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`panel-need-${r.material_code || r.material_id}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono text-[10px] text-slate-500 truncate">{r.material_code || "—"}</span>
                      <span className={`text-[9px] font-semibold px-1.5 py-0 rounded-full border ${prioClass}`}>{prioLabel}</span>
                    </div>
                    <div className="text-xs text-slate-700 dark:text-slate-300 truncate">{r.material_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono flex justify-between">
                      <span>Eksik: <b className="text-red-600">{r.net_shortage} {r.unit}</b></span>
                      {firstSo && <span className="text-indigo-600 truncate ml-2">{firstSo.so_no}{(r.sales_orders || []).length > 1 ? ` +${r.sales_orders.length - 1}` : ""}</span>}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </Card>
    ),
    meetings: (
      <div className="mb-4" data-testid="meetings-widget-slot">
        <MeetingsSummaryCard />
      </div>
    ),
    fx: (
      <Card className="p-3 mb-4 bg-gradient-to-r from-indigo-50/70 to-blue-50/70 dark:from-indigo-900/20 dark:to-blue-900/20 border-indigo-200/60 dark:border-indigo-500/30" data-testid="fx-converter">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 pr-2 border-r border-indigo-200 dark:border-indigo-500/30">
            <Euro size={14} className="text-indigo-600 dark:text-indigo-400"/>
            <span className="text-xs font-semibold text-indigo-900 dark:text-indigo-300">Kur Dönüştürücü</span>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Tutar</label>
            <input
              type="number"
              step="0.01"
              value={fxAmount}
              onChange={e => setFxAmount(e.target.value)}
              className="w-28 px-2 py-1 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/70 dark:text-slate-100 rounded font-mono"
              data-testid="fx-input-amount"
            />
          </div>
          <select
            value={fxBase}
            onChange={e => setFxBase(e.target.value)}
            className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/70 dark:text-slate-100 rounded"
            data-testid="fx-select-base"
          >
            <option value="TRY">TRY</option>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
          </select>
          <ChevronRight size={12} className="text-slate-400"/>
          <select
            value={fxQuote}
            onChange={e => setFxQuote(e.target.value)}
            className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/70 dark:text-slate-100 rounded"
            data-testid="fx-select-quote"
          >
            <option value="TRY">TRY</option>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
          </select>
          <div className="flex items-center gap-1.5" data-testid="fx-result">
            <span className="text-xs uppercase text-slate-500 dark:text-slate-400">Sonuç</span>
            <span className="font-mono text-sm font-semibold text-indigo-900 dark:text-indigo-200 min-w-[7rem] text-right" data-testid="fx-result-value">
              {fxLoading ? "…" : (fxResult ? Number(fxResult.converted).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : "—")}
            </span>
            <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">{fxQuote}</span>
          </div>
          {fxResult && !fxError && (
            <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono" data-testid="fx-rate-line">
              1 {fxBase} = {Number(fxResult.rate || 0).toFixed(4)} {fxQuote}
            </span>
          )}
          <span className="ml-auto text-[10px] text-slate-400 dark:text-slate-500" data-testid="fx-source-info">
            {fxError ? <span className="text-rose-600">{String(fxError)}</span> : (
              <>Kaynak: <b className="text-slate-600 dark:text-slate-300">TCMB</b>{fxResult?.rate_type ? ` (${fxResult.rate_type})` : ""} · Tarih: <span className="font-mono">{fxResult?.rate_date || "—"}</span></>
            )}
          </span>
        </div>
      </Card>
    ),
    kpi: (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4" data-testid="kpi-grid">
        {cards.map((c) => {
          const Icon = c.icon;
          const val = data[c.key] ?? 0;
          return (
            <Link to={c.to} key={c.key} data-testid={`dash-${c.key}`}>
              <Card className="p-4 hover:border-indigo-300 dark:hover:border-violet-500/50 transition-colors cursor-pointer">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{c.label}</div>
                  <Icon size={16} className={c.color} />
                </div>
                <div className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>{val}</div>
              </Card>
            </Link>
          );
        })}
      </div>
    ),
    previews: (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4" data-testid="previews-grid">
        <PreviewCard
          title="Talepler (Kullanıcı)" icon={ListTodo} to="/requests" testid="prev-requests"
          rows={requests.filter(r => r.status !== "completed" && r.status !== "cancelled" && !r.capa_auto)}
          columns={[
            { label: "No", render: r => <span className="font-mono">{r.number}</span> },
            { label: "Başlık", key: "title" },
            { label: "Öncelik", render: r => <Badge level={r.priority === "critical" ? "missing" : r.priority === "high" ? "attention" : "ok"}>{r.priority}</Badge> },
            { label: "Durum", render: r => <Badge level={r.status === "open" ? "attention" : "ok"}>{r.status}</Badge> },
          ]}
        />
        <PreviewCard
          title="Üretim Emirleri" icon={Factory} to="/production" testid="prev-production"
          rows={prodOrders.filter(p => p.status !== "completed" && p.status !== "cancelled")}
          columns={[
            { label: "Üretim No", render: r => <span className="font-mono">{r.prod_no}</span> },
            { label: "Ürün", render: r => pname(r.product_id) },
            { label: "Plan", render: r => `${r.planned_units || 0}` },
            { label: "Durum", render: r => <Badge level={r.status === "completed" ? "ok" : "attention"}>{PROD_STATUS_LABEL?.[r.status] || r.status}</Badge> },
          ]}
        />
        <PreviewCard
          title="Bitmiş Ürünler" icon={Package} to="/finished-products" testid="prev-finished"
          rows={finished.filter(f => f.qty_available > 0)}
          columns={[
            { label: "Lot", render: r => <span className="font-mono">{r.lot_code}</span> },
            { label: "Ürün", render: r => pname(r.product_id) },
            { label: "Müşteri", render: r => cname(r.customer_id) },
            { label: "Stok", key: "qty_available" },
          ]}
        />
        <PreviewCard
          title="Envanter Yönetimi" icon={Boxes} to="/inventory" testid="prev-inventory"
          rows={lots.filter(l => (l.qty_available || 0) > 0)}
          columns={[
            { label: "Lot", render: r => <span className="font-mono">{r.lot_code}</span> },
            { label: "Malzeme", render: r => mname(r.material_id) },
            { label: "Mevcut", key: "qty_available" },
            { label: "Durum", render: r => <Badge level={r.status === "approved" ? "ok" : "attention"}>{r.status}</Badge> },
          ]}
        />
      </div>
    ),
    profit_period: isAdmin ? (
      <div className="flex items-center gap-2 mb-3 mt-8" data-testid="profit-period-row">
        <TrendingUp size={16} className="text-emerald-600 dark:text-emerald-400"/>
        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>Kârlılık Analizi</h2>
        <div className="ml-auto inline-flex items-center gap-1 border border-slate-200 dark:border-slate-700 rounded-md bg-white/70 dark:bg-slate-800/70 p-0.5" data-testid="profit-period">
          {periodOptions.map(o => (
            <button key={o.key} onClick={() => setPeriod(o.key)} data-testid={`profit-period-${o.key}`}
              className={`text-[11px] font-semibold px-2.5 py-0.5 rounded ${period === o.key ? "bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100"}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
    ) : null,
    profit_summary: (isAdmin && labor) ? (
      <Card className="p-4 mb-4 bg-gradient-to-r from-emerald-50/70 to-teal-50/70 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200/60 dark:border-emerald-500/30" data-testid="profit-summary">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} className="text-emerald-700 dark:text-emerald-400"/>
            <span className="text-sm font-semibold text-emerald-900 dark:text-emerald-300">Kârlılık Özeti ({periodOptions.find(p => p.key === period)?.label})</span>
            <Link to="/finance" className="ml-auto"><Btn size="sm" variant="secondary">Detay <ArrowRight size={12}/></Btn></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">İşçilik / Adet</div><div className="font-mono font-semibold text-amber-700 dark:text-amber-400">{(labor.labor_per_unit_eur || 0).toFixed(4)} EUR</div></div>
            <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Günlük Ort. Gider</div><div className="font-mono font-semibold text-slate-800 dark:text-slate-200">{(labor.daily_avg_expense_eur || 0).toFixed(2)} EUR</div></div>
            <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Günlük Ort. Üretim</div><div className="font-mono font-semibold text-slate-800 dark:text-slate-200">{(labor.daily_avg_units || 0).toFixed(2)} adet</div></div>
            <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Aktif Gün</div><div className="font-mono font-semibold text-slate-800 dark:text-slate-200">{labor.active_days || 0}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Kârlı Formül</div><div className="font-mono font-semibold text-emerald-700 dark:text-emerald-400">{formulas.filter(f => {
              const prod = products.find(p => p.id === f.product_id);
              const fq = prod?.fill_qty || 0; const fu = prod?.fill_unit || "ml";
              const cpk = f.cost?.cost_per_kg || 0;
              const pk = f.cost?.packaging_cost_per_unit || 0;
              const raw = fu === "ml" ? cpk * (f.density || 1) * fq / 1000 : cpk * fq / 1000;
              const internal = raw + pk;
              const sales = f.sales_price_eur || 0;
              return sales > 0 && (sales - internal - (labor.labor_per_unit_eur || 0)) > 0;
            }).length} / {formulas.filter(f => (f.sales_price_eur || 0) > 0).length}</div></div>
          </div>
        </Card>
    ) : null,
    profit_trend: (isAdmin && trend && trend.trend && trend.trend.length > 0) ? (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4" data-testid="profit-trend-section">
          <Card className="lg:col-span-2 p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={16} className="text-indigo-600 dark:text-indigo-400"/>
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>Aylık Kârlılık Trendi ({periodOptions.find(p => p.key === period)?.label})</h3>
              <span className="ml-auto text-[10px] text-slate-400 dark:text-slate-500 font-mono">EUR</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trend.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="month" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }}/>
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => `${v.toFixed(2)} EUR`}/>
                <Legend wrapperStyle={{ fontSize: 11 }}/>
                <Bar dataKey="revenue_eur" name="Gelir" fill="#6366f1"/>
                <Bar dataKey="direct_cost_eur" name="Direkt Maliyet" fill="#f59e0b"/>
                <Bar dataKey="net_profit_eur" name="Net Kâr" fill="#10b981"/>
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-4">
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>En Kârlı Formüller</div>
            {(trend.top || []).length === 0 && <div className="text-xs text-slate-400 py-3">Satış fiyatı girilmiş formül yok</div>}
            {(trend.top || []).slice(0, 5).map(t => (
              <div key={t.formula_id} className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/40 py-1.5 text-xs">
                <div className="truncate mr-2 text-slate-700 dark:text-slate-300"><span className="font-mono text-[10px] text-slate-500">{t.code}</span> {t.name}</div>
                <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-400">%{t.margin_pct.toFixed(1)}</span>
              </div>
            ))}
            {(trend.bottom || []).length > 0 && <>
              <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 mt-3 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>En Az Kârlı</div>
              {trend.bottom.slice(0, 3).map(t => (
                <div key={t.formula_id} className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/40 py-1.5 text-xs">
                  <div className="truncate mr-2 text-slate-700 dark:text-slate-300"><span className="font-mono text-[10px] text-slate-500">{t.code}</span> {t.name}</div>
                  <span className={`font-mono font-semibold ${t.margin_pct >= 0 ? "text-amber-700 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`}>%{t.margin_pct.toFixed(1)}</span>
                </div>
              ))}
            </>}
          </Card>
      </div>
    ) : null,
    gmp: (
      <div className="mb-4 mt-6" data-testid="gmp-calendar-wrapper">
        <GMPCalendar />
      </div>
    ),
    checks: (
      <div className="mb-4" data-testid="checks-calendar-wrapper">
        <ChecksCalendar />
      </div>
    ),
    reminders: (
      <div className="mb-4" data-testid="dashboard-reminders-wrapper">
        <UyarilarWidget title="Uyarılar"/>
      </div>
    ),
  };

  return (
    <div>
      <PageHeader title={scope === "user_control" ? "Kullanıcı Kontrol Paneli" : "Kontrol Paneli"} description="Operasyonun anlık durumu"
        actions={
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <StorageHealthPills/>
            {canEdit && (
              <WidgetEditButton editMode={editMode} setEditMode={setEditMode} resetOrder={resetOrder}/>
            )}
          </div>
        } />

      {editMode && (
        <div className="mb-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-500/30 rounded-lg p-3 text-xs text-indigo-800 dark:text-indigo-300" data-testid="widget-edit-hint">
          ⇅ Sürükle-bırak veya oklarla sıralayın · Boyut butonuyla S / M / L / FULL değiştirin · 👁 ile gizleyin. Değişiklikler otomatik kaydedilir.
        </div>
      )}

      <div className="grid grid-cols-4 gap-4">
        {order.map((k, idx) => {
          const content = widgets[k];
          if (!content) return null;
          return (
            <WidgetWrapper key={k} id={k} title={WIDGET_TITLES[k] || k}
              editMode={editMode} isFirst={idx === 0} isLast={idx === order.length - 1}
              isHidden={hidden.has(k)} size={sizes[k] || "FULL"}
              onMove={move} onToggleHide={toggleHidden} onCycleSize={cycleSize}
              onDragStart={onDragStart(k)} onDragOver={onDragOver} onDrop={onDrop(k)}>
              {content}
            </WidgetWrapper>
          );
        })}
      </div>
      {personnelQuickOpen && (
        <PersonnelQuickAction open={personnelQuickOpen}
          onClose={() => setPersonnelQuickOpen(false)}
          onSaved={() => { api.get("/personnel/alerts").then(setPersonnelAlerts).catch(() => {}); }}/>
      )}
      {personnelDetail && (
        <PersonnelAlertDrawer detail={personnelDetail} onClose={() => setPersonnelDetail(null)}/>
      )}
    </div>
  );
}

/** iter109: Alert count pill used in Personnel dashboard widget. */
function AlertPill({ icon, label, count, onClick, testid }) {
  const has = count > 0;
  return (
    <button onClick={onClick} disabled={!has}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md border text-left transition ${has ? "bg-white/80 border-violet-200 hover:border-violet-400 hover:shadow cursor-pointer" : "bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed"}`}
      data-testid={testid}>
      <span className="text-violet-600">{icon}</span>
      <span className="text-[11px] font-medium text-slate-700 flex-1 truncate">{label}</span>
      <span className={`text-xs font-mono px-1.5 py-0.5 rounded-full ${has ? "bg-violet-600 text-white" : "bg-slate-200 text-slate-500"}`}>{count}</span>
    </button>
  );
}

/** iter109: Drawer showing which personnel a dashboard alert belongs to. */
function PersonnelAlertDrawer({ detail, onClose }) {
  const titleMap = { birthday: "Yaklaşan Doğum Günleri", leave: "Yaklaşan İzinler", anniversary: "Yaklaşan İş Yıldönümleri" };
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="personnel-alert-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose}/>
      <div className="w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl p-5 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{titleMap[detail.kind]}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">×</button>
        </div>
        <div className="space-y-2">
          {detail.items.length === 0 && <div className="text-sm text-slate-400 italic">Kayıt yok</div>}
          {detail.items.map((it, i) => (
            <div key={i} className="p-3 border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50/50 dark:bg-slate-800/50" data-testid={`personnel-alert-item-${i}`}>
              <div className="font-semibold text-slate-800 dark:text-slate-100">{it.employee_name}</div>
              {detail.kind === "birthday" && (
                <div className="text-xs text-slate-500 mt-1">
                  Doğum Günü: <span className="font-mono">{it.event_date}</span> · {it.days_left === 0 ? <b className="text-violet-600">BUGÜN</b> : `${it.days_left} gün kaldı`}
                  {it.age_turning && <span className="ml-2 text-violet-600">{it.age_turning}. yaşını dolduruyor</span>}
                </div>
              )}
              {detail.kind === "leave" && (
                <div className="text-xs text-slate-500 mt-1">
                  {it.kind === "ANNUAL_LEAVE" ? "Yıllık İzin" : "Günlük İzin"} · <span className="font-mono">{it.start_date}</span>
                  {it.end_date && it.end_date !== it.start_date && <span className="font-mono"> → {it.end_date}</span>}
                  <div>{it.days_left === 0 ? <b className="text-violet-600">BUGÜN</b> : `${it.days_left} gün kaldı`} · Gün: {it.days}
                  {it.deduct_from_salary && <span className="ml-2 text-rose-600">(Maaştan kesilir)</span>}</div>
                </div>
              )}
              {detail.kind === "anniversary" && (
                <div className="text-xs text-slate-500 mt-1">
                  İşe Giriş: <span className="font-mono">{it.start_date}</span> · <b>{it.year_completing}. yılını tamamlıyor</b>
                  <div>{it.days_left === 0 ? <b className="text-violet-600">BUGÜN</b> : `${it.days_left} gün kaldı`}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
